/**
 * Check if a field path should be shown based on selected fields
 * @param path The field path to check (e.g., "weapons[0].ammo.heDamage")
 * @param selectedFields Set of selected field paths/patterns
 * @returns true if the field should be shown, false otherwise
 * 
 * Behavior:
 * - If no fields are selected, show all fields (backward compatible)
 * - Exact match: if "weapons" is selected, show "weapons" field
 * - Prefix match: if "weapons" is selected, show "weapons[0].ammo.heDamage"
 * - Wildcard match: if "weapons.*" is selected, show all "weapons[...]" fields
 */
export function shouldShowField(path: string, selectedFields: Set<string>): boolean {
  // If no fields selected, show all (backward compatible)
  if (!selectedFields || selectedFields.size === 0) {
    return true;
  }

  if (!path) {
    return false;
  }

  // Special case: exact match for simple field names like "specialities" or "divisions"
  // Check this BEFORE normalization to avoid any issues
  if (selectedFields.has(path)) {
    return true;
  }

  // Normalize path by converting array indices to wildcards for matching
  // Handle both bracket notation [0] and dot notation .0
  let normalizedPath = path.replace(/\[\d+\]/g, ".*");
  normalizedPath = normalizedPath.replace(/\.\d+(?=\.|$)/g, ".*");

  // Check each selected field pattern
  for (const selectedField of selectedFields) {
    // Normalize selected field pattern
    // Handle both bracket notation [0] and dot notation .0
    let normalizedSelected = selectedField.replace(/\[\d+\]/g, ".*");
    normalizedSelected = normalizedSelected.replace(/\.\d+(?=\.|$)/g, ".*");

    // Exact match
    if (normalizedPath === normalizedSelected) {
      return true;
    }
    
    // Special case: if path is exactly "specialities" or "divisions" and selected field matches
    // This handles cases where normalization might interfere
    if ((path === "specialities" || path === "divisions") && selectedField === path) {
      return true;
    }

    // Ancestor match: if the selected field is nested under `path`,
    // consider `path` as selected enough to display (container visibility).
    // Example: selected `weapons.*.ammo.heDamage` => show `weapons`.
    if (
      normalizedSelected.startsWith(normalizedPath + ".") ||
      normalizedSelected.startsWith(normalizedPath + "[")
    ) {
      return true;
    }

    // Check if path starts with selected field (prefix match)
    // e.g., "weapons" matches "weapons[0].ammo.heDamage"
    // e.g., "weapons.*.ammo" matches "weapons.*.ammo.heDamage"
    if (normalizedPath.startsWith(normalizedSelected + ".") || 
        normalizedPath.startsWith(normalizedSelected + "[")) {
      return true;
    }

    // Check if selected field is a prefix of the path
    // e.g., "weapons" should match "weapons[0].ammo.heDamage"
    const pathSegments = normalizedPath.split(".");
    const selectedSegments = normalizedSelected.split(".");
    
    // Check if all selected segments match the beginning of path segments
    if (selectedSegments.length <= pathSegments.length) {
      let matches = true;
      for (let i = 0; i < selectedSegments.length; i++) {
        const selectedSeg = selectedSegments[i];
        const pathSeg = pathSegments[i];
        
        // Skip if either segment is undefined
        if (!selectedSeg || !pathSeg) {
          matches = false;
          break;
        }
        
        // Handle wildcard in selected pattern
        if (selectedSeg === "*" || selectedSeg === ".*") {
          continue; // Wildcard matches anything
        }
        // Check if segment matches (exact or with wildcard)
        const selectedSegment = selectedSeg.replace(/\.\*/g, "");
        const pathSegment = pathSeg.replace(/\.\*/g, "");
        if (selectedSegment !== pathSegment) {
          matches = false;
          break;
        }
      }
      if (matches) {
        return true;
      }
    }

    // Wildcard pattern matching
    if (normalizedSelected.includes("*")) {
      // Convert pattern to regex
      const regexPattern = normalizedSelected
        .replace(/\./g, "\\.")
        .replace(/\*/g, "[^.]*"); // Match any characters except dot
      const regex = new RegExp(`^${regexPattern}`);
      if (regex.test(normalizedPath)) {
        return true;
      }
    }
  }

  return false;
}

